import {
    clearAuthentication,
    getAccessToken,
    getAuthenticatedUsername,
    getCsrfToken,
    hasSecureSessionState
} from "./auth";

export class FantasyApiError extends Error {
    public readonly status: number;

    constructor(
        message: string,
        status: number
    ) {
        super(message);

        this.name = "FantasyApiError";

        this.status = status;
    }
}

function getApiBaseUrl(): string {
    const configuredUrl = String(
        process.env.REACT_APP_FANTASY_API_BASE_URL || ""
    ).trim();

    if (configuredUrl !== "") {
        return configuredUrl.replace(/\/+$/, "");
    }

    return (
        process.env.PUBLIC_URL || ""
    ) + "/create_league";
}

function getApiUrl(path: string): string {
    return getApiBaseUrl() +
        "/" +
        path.replace(/^\/+/, "");
}

async function parseApiResponse<T>(
    response: Response
): Promise<T> {
    const text = await response.text();

    let result: T & {
        success?: boolean;
        message?: string;
    };

    try {
        result = text
            ? JSON.parse(text)
            : {};
    } catch {
        throw new Error(
            text ||
            "El servidor no devolvió JSON válido."
        );
    }

    if (response.status === 401) {
        clearAuthentication();
    }

    if (
        !response.ok ||
        result.success === false
    ) {
        throw new FantasyApiError(
            result.message ||
                "No fue posible completar la solicitud.",
            response.status
        );
    }

    return result;
}

function getTemporaryLegacyHeaders():
    Record<string, string> {
    /*
        Si la app ya tiene sesión PHP validada y token CSRF,
        no envía identificadores legacy como credenciales.
    */
    if (hasSecureSessionState()) {
        return {};
    }

    const accessToken = getAccessToken() || "";

    const accessUsername =
        getAuthenticatedUsername() || "";

    if (!accessToken || !accessUsername) {
        return {};
    }

    /*
        Compatibilidad limitada para clientes/pantallas antiguas.
        Debe eliminarse después de la fecha de transición.
    */
    return {
        "X-Access-Token": accessToken,
        "X-Access-Username": accessUsername
    };
}

interface FantasyApiGetOptions {
    signal?: AbortSignal;
}

export async function fantasyApiGet<T>(
    path: string,
    options: FantasyApiGetOptions = {}
): Promise<T> {
    const response = await fetch(
        getApiUrl(path),
        {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            signal: options.signal,
            headers: {
                Accept: "application/json",
                ...getTemporaryLegacyHeaders()
            }
        }
    );

    return parseApiResponse<T>(response);
}

export async function fantasyApiPost<T>(
    path: string,
    body: unknown
): Promise<T> {
    const csrfToken = getCsrfToken();

    const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...getTemporaryLegacyHeaders()
    };

    if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(
        getApiUrl(path),
        {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers,
            body: JSON.stringify(body)
        }
    );

    return parseApiResponse<T>(response);
}